import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Check, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import CtaLink from '@/components/generals/cta-link'
import SectionIntro from '@/components/landing/section-intro'
import { appLinks } from '@/constants/site'

interface Plan {
    id: string
    eyebrow: string
    name: string
    tagline: string
    features: string[]
    cta: string
    pro?: boolean
}

const PLANS: Plan[] = [
    {
        id: 'free',
        eyebrow: 'The essentials',
        name: 'Free',
        tagline: 'Play and organize your first videos.',
        features: [
            'Classic 1, 2 and 4-view grids',
            'Local videos and photos',
            'No ads during local playback',
            'Export up to 30 seconds at 720p'
        ],
        cta: 'Get it on Google Play'
    },
    {
        id: 'pro',
        eyebrow: 'More views & tools',
        name: 'PRO',
        tagline: 'Monthly, annual or lifetime.',
        features: [
            'Layouts with up to 12 views',
            'PRO layouts and creative tools',
            'Connection with the Google TV app',
            'Local export up to 1080p'
        ],
        cta: 'See PRO in the app',
        pro: true
    }
]

const PricingSection: FC = (): ReactNode => {
    return (
        <section id='pricing' aria-labelledby='pricing-title' className='border-t border-border bg-card py-20 lg:py-28'>
            <div className='mx-auto max-w-5xl px-5 sm:px-8'>
                <SectionIntro
                    id='pricing-title'
                    align='center'
                    eyebrow='Free & PRO'
                    title={(
                        <>
                            Start free.
                            <br />
                            Expand with PRO.
                        </>
                    )}
                    intro='Try local playback and the classic grids. Unlock more tools when you need them.'
                />

                <div className='mt-12 grid gap-5 md:grid-cols-2'>
                    {PLANS.map((plan) => (
                        <article
                            key={plan.id}
                            aria-labelledby={`plan-${plan.id}`}
                            className={cn(
                                'reveal flex flex-col rounded-2xl border p-7 sm:p-8',
                                plan.pro
                                    ? 'border-pro/40 bg-[linear-gradient(160deg,color-mix(in_oklab,var(--pro)_8%,var(--card)),var(--card)_65%)] shadow-[0_28px_60px_-36px_rgb(109_40_217/0.55)]'
                                    : 'border-border bg-secondary/70'
                            )}
                        >
                            <p className={cn('text-[0.72rem] font-bold uppercase tracking-[0.16em]', plan.pro ? 'text-pro-text' : 'text-brand')}>
                                {plan.eyebrow}
                            </p>

                            <h3 id={`plan-${plan.id}`} className='mt-5 font-heading text-[2rem] font-extrabold leading-none tracking-[-0.035em] text-foreground'>
                                MultiScreen{' '}
                                <span className={plan.pro ? 'text-pro-text' : 'text-brand'}>
                                    {plan.name}
                                </span>
                            </h3>

                            <p className='mt-3 text-[0.97rem] text-muted-foreground'>
                                {plan.tagline}
                            </p>

                            <ul className='mt-7 flex-1 space-y-3.5'>
                                {plan.features.map((feature) => (
                                    <li key={feature} className='flex items-center gap-3 text-[0.95rem] text-foreground/90'>
                                        <Check className={cn('size-4 shrink-0', plan.pro ? 'text-pro-text' : 'text-brand')} strokeWidth={2.5} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <CtaLink
                                href={appLinks.playStore}
                                external
                                variant={plan.pro ? 'pro' : 'outline'}
                                className='mt-9 w-full'
                            >
                                {plan.cta}
                            </CtaLink>
                        </article>
                    ))}
                </div>

                <div className='mt-8 flex gap-3.5 text-[0.8rem] leading-relaxed text-muted-foreground'>
                    <Info className='mt-0.5 size-4 shrink-0' />

                    <div className='space-y-1.5'>
                        <p>
                            Choose monthly or annual PRO with automatic renewal, or a lifetime one-time purchase. Check local prices and any eligible offers in the app before confirming.
                        </p>

                        <p>
                            In supported app versions, an optional rewarded ad enables one 1080p export up to 30 seconds of a free composition with compatible local sources. It does not unlock PRO; ad availability may vary.
                        </p>

                        <p>
                            <a
                                href={appLinks.planTerms}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 font-medium text-brand underline-offset-4 hover:underline'
                            >
                                See plan and reward terms
                                <ArrowUpRight className='size-3.5' />
                                <span className='sr-only'>(opens in a new tab)</span>
                            </a>
                            {' '}
                            Download availability depends on your country and account.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default PricingSection
